import React, { useMemo } from "react";
import { Link } from "react-router-dom";

import { IonButton, IonCard, IonCardContent, IonCol, IonGrid, IonRow } from "@ionic/react";

import { Image } from "../../../../../components/Image/Image";
import { CartUnavailableProductProps } from "@core/types";
import { AttributeBadge } from "../../../../../fsd/shared/AttributeBadge/AttributeBadge";
import { useConfig } from "../../../../../providers/config";

import classNames from "classnames/bind";
import styles from "./CartUnavailableProduct.module.scss";
import { getPreviewImage } from "../../../../../utils/getPreviewImage";

const cx = classNames.bind(styles);

const CartUnavailableProduct = ({
  product,
  cartItem,
  className = "",
  removeUnavailableProduct,
  removeProductCardTxtClassName,
  removeProductCardTxt,
}: CartUnavailableProductProps) => {
  const { ignoredProductAttributes, showParentName } = useConfig();
  const hasAttributes = !!cartItem.attributes?.length;
  const showOriginalQuantity = cartItem.originalQuantity > cartItem.quantity;

  const linkToVariationProduct = useMemo(() => {
    let link = `/product-detail/${encodeURI(cartItem.parentId)}`;

    if (cartItem.parentId !== cartItem.id && cartItem.attributes?.length) {
      link += cartItem.attributes
        ?.reduce((url: string, { option, name }) => {
          return `${url}&${encodeURI(name)}=${encodeURI(option ?? "")}`;
        }, "?")
        .replace("?&", "?");
    }

    return link;
  }, [cartItem]);

  const attributes = useMemo(() => {
    if (!cartItem.attributes) {
      return null;
    }

    const attrs = ignoredProductAttributes
      ? cartItem.attributes.filter(
          (attr) =>
            !ignoredProductAttributes.some((ignoredAttr) => ignoredAttr.toLowerCase() === attr.name.toLowerCase()),
        )
      : cartItem.attributes;

    return attrs.map((attr) => {
      return (
        <div key={attr.id} className="flex items-center content-center pl-1 sm:mr-2">
          <div className="pr-1 sm:pr-2">{attr.name}:</div>
          <AttributeBadge value={attr.option ?? ""} isColorPrimary />
        </div>
      );
    });
  }, [cartItem, ignoredProductAttributes]);

  const productImage = getPreviewImage(product?.images?.[0]);

  return (
    <Link to={linkToVariationProduct}>
      <IonCard className={cx("productCard", "ion-no-padding", className)}>
        <IonCardContent className="ion-no-padding">
          <IonGrid className={cx("productContent")}>
            <IonRow className={cx("productContentRow")}>
              <IonCol size={"3"} sizeMd={"2"}>
                <Image className={cx("productContentImg")} image={productImage} />
              </IonCol>
              <IonCol className={cx("productContentDesc")}>
                <IonRow>
                  <IonCol className="ion-text-left">
                    {showParentName && cartItem.parentName ? (
                      <div className="flex flex-col gap-1">
                        <div className={cx("productContentDesc__title-parent")}>{cartItem.parentName}</div>
                        <div className={cx("productContentDesc__title")}>{cartItem.name}</div>
                      </div>
                    ) : (
                      <div className={cx("productContentDesc__title")}>{cartItem.name}</div>
                    )}
                  </IonCol>
                </IonRow>
                {hasAttributes && <IonRow>{attributes}</IonRow>}
                <IonRow
                  className="items-center"
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                >
                  <IonCol>
                    <div className={cx("productCardPrice", "ellipsis !text-base ")}>{cartItem.price} ₽</div>
                  </IonCol>
                  <IonRow className="items-center gap-1">
                    <div className={removeProductCardTxtClassName}>{removeProductCardTxt}</div>
                    {showOriginalQuantity && (
                      <div className="mr-2 line-through font-bold text-[--ion-color-danger-shade]">
                        {cartItem.originalQuantity}
                      </div>
                    )}
                    <IonButton
                      fill="outline"
                      size="small"
                      color="secondary"
                      onClick={(e) => {
                        removeUnavailableProduct(cartItem.id);
                      }}
                    >
                      <p>-</p>
                    </IonButton>
                  </IonRow>
                </IonRow>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonCardContent>
      </IonCard>
    </Link>
  );
};

export default CartUnavailableProduct;
